import { useState, useEffect } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LayoutCard } from "./EditLayoutPage";
import DashboardCardWrapper from "@/components/common/DashboardCardWrapper";
import EditLayoutCardHeader from "@/components/EditLayout/EditLayoutCardHeader";
import { CardContent } from "@/components/ui/card";
import BorderedIconWrapper from "@/components/common/BorderIconWrapper";
import * as dummyData from "@/constants/dummyData";
import { DataTable, Column } from "@/components/common/Table";
import { Typography } from "@/components/ui/typography";
import { TruncatedTextTooltip } from "@/components/common/TextTooltip";
import { Badge } from "@/components/ui/badge";
import { CARD_REGISTRY } from "@/constants/cardListDescription";
import { RiPhoneLine, RiMapPinLine } from "@remixicon/react";
import DashboardFieldIcon from "@/components/Dashboard/common/DashboardFieldIcon";
import { getInitialsFromName } from "@/constants/utilityFunctions";

import adtAlertsIcon from "@/assets/svg/adtAlerts.svg";
import RedUp from "@/assets/svg/risk-stratification-red-up-graph.svg";
import RedDown from "@/assets/svg/risk-stratification-red-down-graph.svg";
import NoChangeRed from "@/assets/svg/noChangeRed.svg";
import NoChangeGreen from "@/assets/svg/noChangeGreen.svg";
import GreenUp from "@/assets/svg/risk-stratification-green-up-graph.svg";
import GreenDown from "@/assets/svg/risk-stratification-green-down-graph.svg";

// Support different card types
type CardType = "keyValue" | "table" | "tickList";

interface SortableCardProps {
  card: LayoutCard;
  isDragOverlay?: boolean;
  type?: CardType;
  dataKey?: string;
  columns?: string[];
  showStar?: boolean;
  isFavourite?: boolean;
  onToggleFavourite?: () => void;
}


// Mapping for display labels (used in tickList and keyValue)
const DISPLAY_LABELS: Record<string, string> = {
  anxiety: "Anxiety",
  depression: "Depression",
  suAlcohol: "Substance Use Disorder - Alcohol",
  suOther: "Substance Use Disorder - Other",
  suOpioids: "Substance Use Disorder - Opiods",
  smi: "Serious Mental Illness",
  schizophrenia: "Schizophrenia",
  dementia: "Dementia",
  bipolarDisorder: "Bipolar Disorder",
  otherMoodDisorders: "Other Mood Disorders",
  idd: "I/DD (Intellectual and Developmental Disabilities)",
  adhd: "ADHD",
  eatingDisorders: "Eating Disorders",
  autism: "Autism",
  suicidalIdeations: "Suicidal Ideations",
  otherBh: "Other BH",
  eventDate: "Event Date",
  eventType: "Event Type",
  eventFacility: "Facility",
  date: "Date",
  type: "Type",
  facility: "Facility",
  educationLiteracy: "Education/Literacy",
  employment: "Employment",
  housingShelter: "Housing Shelter",
  transportation: "Transportation",
  foodResources: "Food Resources",
  financial: "Financial",
  safetyConcerns: "Safety Concerns",
  asthma: "Asthma",
  cancer: "Cancer",
  copd: "COPD",
  diabetes: "Diabetes",
  chronicKidneyDisease: "Chronic Kidney Disease",
  heartDisease: "Heart Disease",
  obesity: "Obesity",
  arthritis: "Arthritis",
  hypertension: "Hypertension",
  stroke: "Stroke",
  osteoporosis: "Osteoporosis",
  hepatitis: "Hepatitis",
  hiv: "HIV/AIDS",
  thyroidDisorders: "Thyroid Disorders",
  parkinsons: "Parkinson's Disease",
  multipleSclerosis: "Multiple Sclerosis",
  fillDate: "Fill Date",
  daysSupply: "Days Supply",
  lastDose: "Last Dose",
  referralDate: "Referral Date",
  sendingFacility: "Sending Facility",
  receivingFacility: "Receiving Facility",
  referralType: "Referral Type",
  referralStatus: "Status",
  vaccine: "Vaccines",
  prescriber: "Prescriber",
  pharmacy: "Pharmacy",
  class: "Class",
  drug: "Drug",
  metricDescription: "Metric",
  metricStatus: "Status",
  riskDriver: "Risk Drivers",
  trend: "Trend",
  diagnosis: "Event Description",
};

const CARD_CONTENT_HEIGHTS: Record<string, string> = {
  ADT_ALERTS: "lg:h-[312px] md:h-[558px]",
  RISK_STRATIFICATION: "h-[312px] overflow-hidden",
  CARE_GAPS: "lg:h-[312px] md:h-[312px]",
  BEHAVIORAL_HEALTH_DIAGNOSES: "lg:h-[492px] md:h-[604px]",
  MEDICAL_DIAGNOSES: "lg:h-[492px] md:h-[404px]",
  SDOH_BARRIERS: "lg:h-[492px] md:h-[287px]",
  RECENT_VISITS_ENCOUNTERS: "lg:h-[304px] md:h-[304px]",
  TWELVE_MONTH_COST_UTILIZATION: "lg:h-[318px] md:h-[304px]",
  MEDICATION_FILL_HISTORY: "lg:h-[483px] md:h-[483px]",
  IMMUNIZATIONS: "lg:h-[520px] md:h-[576px]",
  REFERRALS: "lg:h-[520px] md:h-[520px]",
  PCP: "lg:h-[257px] md:h-[230px]",
  HEALTH_PLAN: "lg:h-[210px] md:h-[210px]",
};

const TABLE_MAX_HEIGHTS: Record<string, string> = {
  ADT_ALERTS: "288px",
  RISK_STRATIFICATION: "280px",
  CARE_GAPS: "280px",
  RECENT_VISITS_ENCOUNTERS: "247px",
  MEDICATION_FILL_HISTORY: "451px",
  IMMUNIZATIONS: "454px",
  REFERRALS: "500px",
};

const SortableCard: React.FC<SortableCardProps> = ({
  card,
  isDragOverlay = false,
  type = "keyValue",
  dataKey,
  columns = [],
  showStar = false,
  isFavourite = false,
  onToggleFavourite,
}) => {
  const [isMouseDown, setIsMouseDown] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card?.instanceId });

  // dnd-kit captures the pointer during a drag, so mouseup never fires on the
  // card element. Reset isMouseDown when the drag ends so the cursor reverts
  // to grab immediately instead of waiting for the next click.
  useEffect(() => {
    if (!isDragging) setIsMouseDown(false);
  }, [isDragging]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const cardConfig = CARD_REGISTRY[card?.id];
  const iconLight = cardConfig?.icon?.light || adtAlertsIcon;
  const iconDark = cardConfig?.icon?.dark || adtAlertsIcon;

  // Fetch data from dummyData.ts based on dataKey
  const rawData = dataKey ? (dummyData as any)[dataKey] : null;

  const renderContent = () => {
    if (!rawData) {
      return (
        <div className="flex items-center justify-center h-20">
          <Typography variant="medium-14" className="text-grey-500">
            No data available
          </Typography>
        </div>
      );
    }

    if (type === "table" && Array.isArray(rawData)) {
      const tableColumns: Column<any>[] = columns.map((colKey) => ({
        key: colKey,
        label: DISPLAY_LABELS[colKey] || colKey,
        isSortable: false,
        render: (row: any) => {
          const value = row[colKey];

          // Specialized rendering for Care Gaps status
          if (colKey === "metricStatus") {
            const isSuccess = value === "Met";
            return (
              <div
                className={`
                  inline-flex items-center justify-center
                  px-2 py-1 rounded-[4px] h-[24px] w-[72px]
                  border whitespace-nowrap text-[14px] font-medium
                  ${isSuccess
                    ? "border-green-700 text-green-700"
                    : "border-[var(--declined-bg)] text-[var(--declined-bg)]"
                  }
                `}
              >
                {value}
              </div>
            );
          }

          // Specialized rendering for Referral status
          if (colKey === "referralStatus") {
            const status = value || "-";
            const statusClassMap: Record<string, string> = {
              Submitted: "bg-[var(--submitted-bg)] text-[var(--submitted-text)]",
              Campaigned: "bg-[var(--submitted-bg)] text-[var(--submitted-text)]",
              Offered: "bg-[var(--submitted-bg)] text-[var(--submitted-text)]",
              Accepted: "bg-[var(--accepted-bg)] text-[var(--accepted-text)]",
              Scheduled: "bg-[var(--accepted-bg)] text-[var(--accepted-text)]",
              Rescheduled: "bg-[var(--accepted-bg)] text-[var(--accepted-text)]",
              Declined: "bg-[var(--declined-bg)] text-[var(--declined-text)]",
              "No Show": "bg-[var(--declined-bg)] text-[var(--declined-text)]",
              Waitlisted: "bg-[var(--waitlisted-bg)] text-[var(--waitlisted-text)]",
              Visited: "bg-[var(--visited-bg)] text-[var(--visited-text)]",
              "Member Admitted": "bg-[var(--visited-bg)] text-[var(--visited-text)]",
              Admitted: "bg-[var(--visited-bg)] text-[var(--visited-text)]",
              "Diversion Complete": "bg-[var(--visited-bg)] text-[var(--visited-text)]",
              "Report Shared": "bg-[var(--visited-bg)] text-[var(--visited-text)]",
              Cancelled: "bg-[var(--cancelled-bg)] text-[var(--cancelled-text)]",
              Withdrawn: "bg-[var(--cancelled-bg)] text-[var(--cancelled-text)]",
            };
            const classes = statusClassMap[status] ?? "bg-gray-400 text-white";

            return (
              <Badge
                className={`h-[24px] px-[8px] min-w-[100px] max-w-[150px] rounded-[4px] border-none text-[14px] leading-[20px] font-medium font-inter inline-flex items-center justify-center whitespace-nowrap ${classes}`}
              >
                {status}
              </Badge>
            );
          }

          // Specialized rendering for Risk Stratification trend
          if (colKey === "trend") {
            const colorLower = String(row.color || "").toLowerCase();
            const trendValue = String(value || "").toLowerCase();
            const isGreen = colorLower === "green";

            let trendImage: string | null = null;
            if (isGreen) {
              if (trendValue === "up") trendImage = GreenUp;
              else if (trendValue === "down") trendImage = GreenDown;
              else if (trendValue === "no change") trendImage = NoChangeGreen;
            } else {
              if (trendValue === "no change") trendImage = NoChangeRed;
              else if (trendValue === "up") trendImage = RedUp;
              else trendImage = RedDown;
            }

            if (trendImage) {
              return (
                <div className="flex items-center justify-start">
                  <img src={trendImage} alt="trend" className="h-[20px] w-auto" />
                </div>
              );
            }
            return value;
          }

          return value || "-";
        },
      }));

      const dataWithId = rawData.map((item, index) => ({
        ...item,
        id: index + 1,
      }));

      return (
        <div className="w-full">
          <DataTable
            data={{ data: dataWithId, total: dataWithId.length }}
            columns={tableColumns}
            height="auto"
            maxHeight={TABLE_MAX_HEIGHTS[card?.id] || "300px"}
          />
        </div>
      );
    }

    if (type === "tickList" && typeof rawData === "object") {
      // Sort entries: active (true) values first, then alphabetically within each group
      const entries = Object.entries(rawData as Record<string, boolean>).sort(
        ([keyA, valA], [keyB, valB]) => {
          if (valA !== valB) return valA ? -1 : 1;
          const labelA = DISPLAY_LABELS[keyA] || keyA;
          const labelB = DISPLAY_LABELS[keyB] || keyB;
          return labelA.localeCompare(labelB);
        }
      );

      return (
        <div className="columns-1 md:columns-2 gap-[10px] h-full" style={{ columnFill: "auto" }}>
          {entries.map(([key, value]) => {
            const label = DISPLAY_LABELS[key] || key;
            const isActive = value === true;

            const displayLabel = key === "educationLiteracy" ? (
              <>
                Education/
                <br className='md:hidden' />
                <span className='hidden md:inline'> </span>
                Literacy
              </>
            ) : (
              label
            );

            return (
              <div
                key={key}
                className={`
                  flex items-center justify-between gap-1
                  px-4 py-3 rounded-lg mb-[10px]
                  border border-grey-300 dark:border-grey-600
                  break-inside-avoid
                `}
              >
                <TruncatedTextTooltip text={label} className="flex-1 min-w-0">
                  <Typography
                    variant="medium-14"
                    className={`truncate ${isActive
                      ? "text-grey-700 dark:text-grey-400"
                      : "text-grey-500 dark:text-grey-700"
                      }`}
                  >
                    {displayLabel}
                  </Typography>
                </TruncatedTextTooltip>
                <div className="flex-shrink-0 ml-2">
                  {isActive ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="10" cy="10" r="10" fill="#4F5B66" />
                      <path
                        d="M14.5 7L8.5 13L5.5 10"
                        className="stroke-white dark:stroke-black"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <Typography
                      variant="medium-14"
                      className="text-grey-500 dark:text-grey-700"
                    >
                      --
                    </Typography>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Default: keyValue
    const entries = Object.entries(rawData as Record<string, string>);

    // Custom PCP Rendering
    const isPCP = card?.id === "PCP";
    if (isPCP) {
      const pcpName = entries.find(([k]) => k === "PCP Name")?.[1] || "-";
      const fieldIconMap: Record<string, any> = {
        "Phone Number": RiPhoneLine,
        Address: RiMapPinLine,
        City: "ri-community-line",
        State: "ri-government-line",
      };
      const pcpFields = entries.filter(([k]) => k !== "PCP Name");

      return (
        <div className="flex flex-col">
          <div className='flex items-center gap-3 mb-4'>
            <div className='w-10 h-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center border-[1px] border-grey-300 dark:border-[1px] dark:border-grey-300'>
              <Typography variant='semibold-16' className='text-blue-600'>
                {pcpName && pcpName !== "-"
                  ? getInitialsFromName(pcpName)
                  : "-"}
              </Typography>
            </div>
            <TruncatedTextTooltip text={pcpName || "-"}>
              <Typography
                variant='medium-16'
                className='text-grey-800 dark:text-white truncate'>
                {pcpName || "-"}
              </Typography>
            </TruncatedTextTooltip>
          </div>
          <div className='gap-[16px] flex flex-col'>
            {pcpFields.map(([key, value]) => {
              const IconComponent = fieldIconMap[key];

              return (
                <div key={key} className='flex items-start gap-3'>
                  {IconComponent ? <DashboardFieldIcon icon={IconComponent} /> : <div className="w-5" />}

                  <div className='flex-1 flex justify-between items-start min-w-0'>
                    <Typography
                      variant='medium-14'
                      className='text-grey-700 dark:text-gray-400 flex-shrink-0 leading-[1.6]'>
                      {key}
                    </Typography>

                    <div className='flex items-center gap-2 min-w-0 max-w-[60%]'>
                      <TruncatedTextTooltip
                        text={value ?? ""}
                        className='min-w-0 flex-1'>
                        <Typography
                          variant='medium-14'
                          className='text-gray-800 dark:text-white text-right truncate max-md:whitespace-normal max-md:break-words max-md:text-right max-md:block leading-[1.6]'>
                          {value}
                        </Typography>
                      </TruncatedTextTooltip>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Calculate total if it's cost/utilization data
    const isCostUtil = dataKey === "COST_UTILIZATION_DATA";
    const total = isCostUtil
      ? entries.reduce((acc, [_, val]) => {
        const numericVal = Number(String(val ?? "").replace(/[^0-9.-]+/g, ""));
        return acc + (isNaN(numericVal) ? 0 : numericVal);
      }, 0)
      : 0;

    const formatCurrency = (value: number): string => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
      }).format(value);
    };

    return (
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3">
          {entries.map(([key, value]) => (
            <div key={key} className="flex justify-between items-start gap-4">
              <Typography variant="medium-14" className="text-grey-700 dark:text-grey-400 flex-shrink-0">
                {key}
              </Typography>
              <TruncatedTextTooltip text={value ?? ""} className={`min-w-0 ${key === "Member Status" ? "" : "flex-1"}`}>
                {key === "Member Status" ? (
                  <Badge
                    variant={(value ?? "").toLowerCase() === "active" ? "success" : "warning"}
                    size="sm"
                    className="h-[24px] min-w-[53px] flex items-center justify-center"
                  >
                    {value}
                  </Badge>
                ) : (
                  <Typography variant="medium-14" className="text-grey-800 dark:text-white text-right truncate">
                    {value}
                  </Typography>
                )}
              </TruncatedTextTooltip>
            </div>
          ))}
        </div>

        {isCostUtil && (
          <>
            <div className="border-t border-dashed border-gray-300 dark:border-gray-600 my-1"></div>
            <div className="bg-primary-100 dark:bg-blue-600 rounded-sm p-[8px] h-[40px]">
              <div className="flex justify-between items-center">
                <Typography variant="semibold-14" className="text-primary-600 dark:text-primary-400">
                  TOTAL
                </Typography>
                <Typography variant="semibold-16" className="text-primary-600 dark:text-primary-400">
                  {formatCurrency(total)}
                </Typography>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onMouseDown={() => setIsMouseDown(true)}
      onMouseUp={() => setIsMouseDown(false)}
      className={`w-full ${isMouseDown || isDragging || isDragOverlay ? "cursor-grabbing" : "cursor-grab"} ${isDragOverlay ? "opacity-80" : ""}`}
    >
      <DashboardCardWrapper className="overflow-hidden">
        <EditLayoutCardHeader
          icon={
            <BorderedIconWrapper>
              <img src={iconLight} alt={card.label} className="w-4 h-4 block dark:hidden" />
              <img src={iconDark} alt={card.label} className="w-4 h-4 hidden dark:block" />
            </BorderedIconWrapper>
          }
          title={
            ["HEALTH_PLAN", "RISK_STRATIFICATION", "CARE_GAPS"].includes(card?.id) ? (
              <div className='flex flex-col h-[44px] gap-0 min-w-0 overflow-hidden'>
              <TruncatedTextTooltip text={card.label} className="min-w-0">
                <Typography
                  variant='semibold-16'
                  className='text-grey-800 dark:text-white leading-tight truncate'>
                  {card.label}
                </Typography>
              </TruncatedTextTooltip>
              <TruncatedTextTooltip
                text={`Last updated on ${card?.id === "RISK_STRATIFICATION" ? "10/31/2025" : "01/01/2026"}`}
                className="min-w-0">
                <Typography
                  className='text-grey-600 dark:text-grey-500 text-left truncate'
                  style={{
                    fontFamily: "Inter",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "12px",
                    lineHeight: "20px",
                    letterSpacing: "0px",
                  }}>
                  Last updated on {card?.id === "RISK_STRATIFICATION" ? "10/31/2025" : "01/01/2026"}
                </Typography>
                </TruncatedTextTooltip>
              </div>
            ) : (
              card.label
            )
          }
          className={["HEALTH_PLAN", "RISK_STRATIFICATION", "CARE_GAPS"].includes(card?.id) ? "h-auto py-3" : "h-[56px]"}
          showStar={showStar}
          isFavourite={isFavourite}
          onToggleFavourite={onToggleFavourite}
          chip={card?.id === "RISK_STRATIFICATION" ? {
            key: "Risk Score",
            value: "18%",
            variant: "success",
          } : undefined}
        />
        <CardContent className={`p-4 ${CARD_CONTENT_HEIGHTS[card?.id] || "h-auto"}`}>
          {renderContent()}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default SortableCard;
